import { Button, Empty, Input, Spin, Tag } from "antd";
import { FolderOpenOutlined, SearchOutlined } from "@ant-design/icons";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { TierFilterChips } from "../components/TierFilterChips";
import { CaseTagChip } from "../components/CaseTagsEditor";
import { useWs } from "../hooks/useWs";
import { listCases } from "../api/cases";
import type { CaseStatus, CaseSummary } from "../api/cases";
import { relativeTime } from "../utils/time";

type StatusFilter = "all" | CaseStatus;

export function CasesPage() {
  const navigate = useNavigate();
  const [cases, setCases] = useState<CaseSummary[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<StatusFilter>("all");
  const queryRef = useRef({ status, q });
  queryRef.current = { status, q };

  const refresh = useCallback(async (s: StatusFilter, term: string) => {
    setLoading(true);
    try {
      const { cases, nextCursor } = await listCases({ status: s, q: term || undefined });
      setCases(cases);
      setNextCursor(nextCursor);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMore = useCallback(async () => {
    if (!nextCursor) return;
    setLoadingMore(true);
    try {
      const result = await listCases({
        status: queryRef.current.status,
        q: queryRef.current.q || undefined,
        cursor: nextCursor,
      });
      setCases((prev) => [...prev, ...result.cases]);
      setNextCursor(result.nextCursor);
    } finally {
      setLoadingMore(false);
    }
  }, [nextCursor]);

  useEffect(() => {
    const handle = setTimeout(() => {
      void refresh(status, q);
    }, 200);
    return () => clearTimeout(handle);
  }, [status, q, refresh]);

  useWs((ev) => {
    if (ev.type === "case:new" || ev.type === "case:updated") {
      void refresh(queryRef.current.status, queryRef.current.q);
    }
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 10,
          padding: "12px 16px",
          borderBottom: "1px solid var(--ant-color-border)",
          background: "var(--ant-color-bg-container)",
        }}
      >
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="按关键词、案件号、摘要内容检索"
          prefix={<SearchOutlined style={{ color: "var(--ant-color-text-secondary)" }} />}
          allowClear
          size="large"
        />
        <TierFilterChips<StatusFilter>
          value={status}
          onChange={setStatus}
          options={[
            { label: "全部", value: "all" },
            { label: "进行中", value: "open" },
            { label: "已归档", value: "closed" },
          ]}
        />
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: 16 }}>
        <div style={{ maxWidth: 1440 }}>
          {loading ? (
            <div style={{ display: "grid", placeItems: "center", padding: 48 }}>
              <Spin />
            </div>
          ) : cases.length === 0 ? (
            <Empty
              image={<FolderOpenOutlined style={{ fontSize: 48, color: "var(--ant-color-text-quaternary)" }} />}
              description={
                q ? "没有匹配的案件" : "转发带触发词的邮件即可自动生成案件"
              }
              style={{ paddingTop: 56 }}
            />
          ) : (
            <div
              style={{
                display: "grid",
                // 至少 280px 一列,最多 4 列(由父容器 maxWidth 自然限制)
                gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                gap: 12,
              }}
            >
              {cases.map((c) => (
                <CaseCard key={c.id} c={c} onClick={() => navigate(`/cases/${c.id}`)} />
              ))}
            </div>
          )}
          {nextCursor ? (
            <div style={{ display: "flex", justifyContent: "center", marginTop: 16 }}>
              <Button onClick={loadMore} loading={loadingMore}>
                加载更多
              </Button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function CaseCard({ c, onClick }: { c: CaseSummary; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        textAlign: "left",
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        gap: 8,
        padding: 16,
        border: "1px solid var(--ant-color-border)",
        borderRadius: 14,
        background: "var(--ant-color-bg-container)",
        transition: "box-shadow .15s ease, transform .15s ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = "0 6px 20px rgba(0,0,0,0.08)";
        e.currentTarget.style.transform = "translateY(-1px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = "none";
        e.currentTarget.style.transform = "none";
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <span
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: "var(--ant-color-primary)",
            letterSpacing: 0.3,
          }}
        >
          {c.caseId}
        </span>
        <span style={{ fontSize: 12, color: "var(--ant-color-text-secondary)" }}>
          {relativeTime(c.createdAt)}
        </span>
      </div>
      <div
        style={{
          fontSize: 16,
          fontWeight: 600,
          lineHeight: 1.4,
          overflow: "hidden",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
        }}
      >
        {c.title || "(未命名案件)"}
      </div>
      <div
        style={{
          fontSize: 14,
          color: "var(--ant-color-text-secondary)",
          lineHeight: 1.5,
          minHeight: 21,
          overflow: "hidden",
          display: "-webkit-box",
          WebkitLineClamp: 3,
          WebkitBoxOrient: "vertical",
        }}
      >
        {c.summary || "AI 正在生成摘要…"}
      </div>
      {c.keywords.length > 0 ? (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 2 }}>
          {c.keywords.slice(0, 6).map((k) => (
            <Tag key={k} style={{ marginRight: 0, fontSize: 12 }}>
              {k}
            </Tag>
          ))}
        </div>
      ) : null}
      {c.tags.length > 0 ? (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 2 }}>
          {c.tags.map((t) => (
            <CaseTagChip key={t} text={t} />
          ))}
        </div>
      ) : null}
    </button>
  );
}
